/**
 * Maps legacy template icon names to Iconify icons
 */
export const TEMPLATE_ICON_MAP: Record<string, string> = {
  // Email
  messages: "mdi:email-outline",
  add_mail: "mdi:email-plus-outline",
  alert_mail: "mdi:email-alert-outline",
  send_mail: "mdi:email-send-outline",
  approved_mail: "mdi:email-check-outline",
  bookmark_mail: "mdi:email-mark-as-unread",
  block_mail: "mdi:email-remove-outline",
  cancel_mail: "mdi:email-off-outline",
  edit_mail: "mdi:email-edit-outline",
  forward_mail: "mdi:email-fast-outline",
  help_mail: "mdi:email-question-outline",
  envelope: "mdi:email-outline",
  info_mail: "mdi:email-newsletter-outline",
  like_mail: "mdi:email-heart-outline",
  open_mail: "mdi:email-open-outline",
  protected_mail: "mdi:email-lock-outline",
  receive_mail: "mdi:email-receive-outline",
  remove_mail: "mdi:email-minus-outline",
  reply_mail: "mdi:email-arrow-left-outline",
  search_mail: "mdi:email-search-outline",
  send_mail2: "mdi:email-send-outline",
  share_mail: "mdi:email-variant",
  letter_box: "mdi:mailbox-outline",
  postbox: "mdi:mailbox-up-outline",
  stamp: "mdi:postage-stamp",
  rss: "mdi:rss",

  // Calls
  call_script: "mdi:phone-outline",
  call: "mdi:phone-outline",
  incoming_call: "mdi:phone-incoming",
  outgoing_call: "mdi:phone-outgoing",
  missed_call: "mdi:phone-missed",
  hold_call: "mdi:phone-paused",
  no_call: "mdi:phone-off",
  call_cancel: "mdi:phone-cancel",
  call_ring: "mdi:phone-ring",

  // Social Media
  linkedin: "mdi:linkedin",
  add_linkedin: "mdi:linkedin",
  alert_linkedin: "mdi:linkedin",
  approved_linkedin: "mdi:linkedin",
  block_linkedin: "mdi:linkedin",
  bookmark_linkedin: "mdi:linkedin",

  facebook: "mdi:facebook",
  add_facebook: "mdi:facebook",
  alert_facebook: "mdi:facebook",
  approved_facebook: "mdi:facebook",
  block_facebook: "mdi:facebook",
  bookmark_facebook: "mdi:facebook",

  instagram: "simple-icons:instagram",
  add_instagram: "simple-icons:instagram",
  alert_instagram: "simple-icons:instagram",
  approved_instagram: "simple-icons:instagram",
  block_instagram: "simple-icons:instagram",
  bookmark_instagram: "simple-icons:instagram",

  twitter: "mdi:twitter",
  add_twitter: "mdi:twitter",
  alert_twitter: "mdi:twitter",
  approved_twitter: "mdi:twitter",
  block_twitter: "mdi:twitter",
  bookmark_twitter: "mdi:twitter",

  // General
  others: "mdi:file-document-outline",
  add_other: "mdi:file-document-plus-outline",
  alert_other: "mdi:alert-circle-outline",
  approved_other: "mdi:check-circle-outline",
  bookmark_other: "mdi:bookmark-outline",
  folder: "mdi:folder-outline",
  bookmark: "mdi:bookmark-outline",
  alert: "mdi:alert-circle-outline",
  bookmark_message: "mdi:bookmark-outline",
  computer: "mdi:monitor-outline",
  message: "mdi:message-outline",
  laptop: "mdi:laptop",
  laptop_connection: "mdi:lan-connect",
  star: "mdi:star-outline",
  heart: "mdi:heart-outline",
  calendar: "mdi:calendar-outline",
  chart: "mdi:chart-line",
  briefcase: "mdi:briefcase-outline",
  tag: "mdi:tag-outline",
  pin: "mdi:pin-outline",
  check: "mdi:check-circle-outline",
  clock: "mdi:clock-outline",
  account: "mdi:account-outline",
  cog: "mdi:cog-outline",
  lightbulb: "mdi:lightbulb-outline",
  rocket: "mdi:rocket-launch-outline",
  target: "mdi:target",
};

/**
 * Icon for each TaskType enum value (call, email, linkedIn, whatsApp, sms, other)
 */
export const TASK_TYPE_ICON_MAP: Record<string, string> = {
  call: "mdi:phone-outline",
  email: "mdi:email-outline",
  linkedIn: "mdi:linkedin",
  whatsApp: "mdi:whatsapp",
  sms: "mdi:message-text-outline",
  other: "mdi:clipboard-text-outline",
};

/**
 * Gets the Iconify icon for a task type value (case-insensitive)
 */
export const getTaskTypeIcon = (taskType: string): string => {
  const key = Object.keys(TASK_TYPE_ICON_MAP).find(
    (k) => k.toLowerCase() === String(taskType).toLowerCase()
  );
  return key ? TASK_TYPE_ICON_MAP[key] : "mdi:clipboard-text-outline";
};

/**
 * Gets Iconify icon for a template folder icon name
 */
export const getTemplateIcon = (iconName: string): string => {
  // Remove .png extension if present (for legacy database records)
  const cleanName = iconName.replace(/\.png$/, "");
  return TEMPLATE_ICON_MAP[cleanName] || "mdi:folder-outline";
};

/**
 * Icon categories for IconsModal
 */
export const ICON_CATEGORIES = [
  {
    category: "Email",
    icons: [
      { name: "messages", icon: "mdi:email-outline", label: "Messages" },
      { name: "add_mail", icon: "mdi:email-plus-outline", label: "Add Mail" },
      { name: "alert_mail", icon: "mdi:email-alert-outline", label: "Alert Mail" },
      { name: "send_mail", icon: "mdi:email-send-outline", label: "Send Mail" },
      {
        name: "approved_mail",
        icon: "mdi:email-check-outline",
        label: "Approved Mail",
      },
      {
        name: "bookmark_mail",
        icon: "mdi:email-mark-as-unread",
        label: "Bookmark Mail",
      },
      {
        name: "block_mail",
        icon: "mdi:email-remove-outline",
        label: "Block Mail",
      },
      { name: "reply_mail", icon: "mdi:email-arrow-left-outline", label: "Reply Mail" },
      { name: "forward_mail", icon: "mdi:email-fast-outline", label: "Forward Mail" },
      { name: "edit_mail", icon: "mdi:email-edit-outline", label: "Edit Mail" },
      { name: "open_mail", icon: "mdi:email-open-outline", label: "Open Mail" },
      { name: "search_mail", icon: "mdi:email-search-outline", label: "Search Mail" },
      { name: "envelope", icon: "mdi:email-outline", label: "Envelope" },
      { name: "letter_box", icon: "mdi:mailbox-outline", label: "Letter Box" },
    ],
  },
  {
    category: "Calls",
    icons: [
      { name: "call_script", icon: "mdi:phone-outline", label: "Call Script" },
      {
        name: "incoming_call",
        icon: "mdi:phone-incoming",
        label: "Incoming Call",
      },
      {
        name: "outgoing_call",
        icon: "mdi:phone-outgoing",
        label: "Outgoing Call",
      },
      { name: "missed_call", icon: "mdi:phone-missed", label: "Missed Call" },
      { name: "hold_call", icon: "mdi:phone-paused", label: "Hold Call" },
      { name: "no_call", icon: "mdi:phone-off", label: "No Call" },
      { name: "call_cancel", icon: "mdi:phone-cancel", label: "Call Cancel" },
      { name: "call_ring", icon: "mdi:phone-ring", label: "Call Ring" },
      { name: "call", icon: "mdi:phone-outline", label: "Call" },
    ],
  },
  {
    category: "Social Media",
    icons: [
      { name: "linkedin", icon: "mdi:linkedin", label: "LinkedIn" },
      { name: "facebook", icon: "mdi:facebook", label: "Facebook" },
      {
        name: "instagram",
        icon: "simple-icons:instagram",
        label: "Instagram",
      },
      { name: "twitter", icon: "mdi:twitter", label: "Twitter" },
    ],
  },
  {
    category: "General",
    icons: [
      {
        name: "others",
        icon: "mdi:file-document-outline",
        label: "Others",
      },
      { name: "folder", icon: "mdi:folder-outline", label: "Folder" },
      { name: "bookmark", icon: "mdi:bookmark-outline", label: "Bookmark" },
      {
        name: "alert",
        icon: "mdi:alert-circle-outline",
        label: "Alert",
      },
      { name: "message", icon: "mdi:message-outline", label: "Message" },
      { name: "computer", icon: "mdi:monitor-outline", label: "Computer" },
      { name: "laptop", icon: "mdi:laptop", label: "Laptop" },
      { name: "star", icon: "mdi:star-outline", label: "Star" },
      { name: "heart", icon: "mdi:heart-outline", label: "Heart" },
      { name: "calendar", icon: "mdi:calendar-outline", label: "Calendar" },
      { name: "chart", icon: "mdi:chart-line", label: "Chart" },
      { name: "briefcase", icon: "mdi:briefcase-outline", label: "Briefcase" },
      { name: "tag", icon: "mdi:tag-outline", label: "Tag" },
      { name: "pin", icon: "mdi:pin-outline", label: "Pin" },
      { name: "check", icon: "mdi:check-circle-outline", label: "Check" },
      { name: "clock", icon: "mdi:clock-outline", label: "Clock" },
      { name: "account", icon: "mdi:account-outline", label: "Account" },
      { name: "cog", icon: "mdi:cog-outline", label: "Settings" },
      { name: "lightbulb", icon: "mdi:lightbulb-outline", label: "Idea" },
      { name: "rocket", icon: "mdi:rocket-launch-outline", label: "Rocket" },
      { name: "target", icon: "mdi:target", label: "Target" },
    ],
  },
];
